<template>
  <div class="radioactive-decay-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$N = N₀ (1/2)^(t/T)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>存留核数 N (Remaining)</label>
            <span class="val">{{ activeCount }} / {{ totalAtoms }}</span>
          </div>
          <div class="hud-item orange">
            <label>已过时间 t</label>
            <span class="val">{{ time.toFixed(1) }} <small>units</small></span>
          </div>
        </div>

        <div class="atomic-grid">
          <svg width="100%" height="200" viewBox="0 0 400 200">
            <defs>
               <filter id="glow-decay"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
            </defs>
            <!-- 粒子容器背景 -->
            <rect x="10" y="10" width="380" height="180" rx="12" fill="rgba(30, 41, 59, 0.3)" stroke="#1e293b" />
            
            <!-- 原子核群 -->
            <g v-for="atom in atoms" :key="atom.id">
              <!-- 未衰变母核 -->
              <circle v-if="!atom.decayed" :cx="atom.x" :cy="atom.y" r="4.5" fill="#f43f5e">
                 <animate attributeName="r" values="4.5;5.5;4.5" dur="1s" :begin="Math.random()+'s'" repeatCount="indefinite" />
              </circle>
              <!-- 衰变产物子核 -->
              <circle v-else :cx="atom.x" :cy="atom.y" r="3.5" fill="#475569" opacity="0.4" />
              
              <!-- 衰变瞬间特效 (释放射线) -->
              <g v-if="atom.justDecayed">
                 <circle :cx="atom.x" :cy="atom.y" r="4" fill="none" stroke="#fbbf24" filter="url(#glow-decay)">
                    <animate attributeName="r" from="4" to="20" dur="0.4s" repeatCount="1" />
                    <animate attributeName="opacity" from="1" to="0" dur="0.4s" repeatCount="1" />
                 </circle>
                 <!-- 释放粒子线 -->
                 <line :x1="atom.x" :y1="atom.y" :x2="atom.x + (Math.random()-0.5)*40" :y2="atom.y + (Math.random()-0.5)*40" stroke="#fff" stroke-width="1.5">
                    <animate attributeName="opacity" from="1" to="0" dur="0.3s" repeatCount="1" />
                 </line>
              </g>
            </g>
          </svg>
        </div>

        <!-- 衰变曲线图 (右下角悬浮) -->
        <div class="mini-chart">
           <svg width="140" height="80" viewBox="0 0 140 80">
              <path :d="curvePathMini" fill="none" stroke="#64748b" stroke-width="1" stroke-dasharray="3,2" />
              <path :d="activePathMini" fill="none" stroke="#f43f5e" stroke-width="2.5" />
              <circle :cx="currentX" :cy="currentY" r="3" fill="#fff" filter="url(#glow-decay)" />
           </svg>
           <label>指数衰变规律</label>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 100px">半衰期 T_1/2</span>
                 <el-slider v-model="halfLife" :min="10" :max="120" size="small" class="flex-1"></el-slider>
                 <span class="val-tag">{{ halfLife.toFixed(0) }}</span>
              </div>
              <div class="flex justify-between items-center mt-1">
                 <div class="decay-stats">
                    <span class="hl">活跃度: {{ ( (activeCount/totalAtoms)*100 ).toFixed(0) }}%</span>
                 </div>
                 <el-button type="danger" size="mini" icon="el-icon-refresh" @click="reset">注入新核源</el-button>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>衰变是量子微观下的随机事件，单个核何时衰变完全不可预知。但对于大量核，整体遵循严格的<strong>指数减少律</strong>。每经过一个半衰期，系统能量释放减半。这是碳-14测年和核能动力学的根本依据。<strong>注意：</strong>环境的外界物理干扰无法改变半衰期。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RadioactiveDecayViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      totalAtoms: 120,
      halfLife: 50,
      time: 0,
      atoms: [],
      activeCount: 120,
      running: true
    }
  },
  computed: {
    curvePathMini() {
       let points = [];
       for (let t = 0; t <= 120; t += 5) {
          const n = 60 * Math.pow(0.5, t / this.halfLife);
          points.push(`${10 + t},${70 - n}`);
       }
       return "M " + points.join(" L ");
    },
    activePathMini() {
       let points = [];
       for (let t = 0; t <= Math.min(120, this.time); t += 2) {
          const n = 60 * Math.pow(0.5, t / this.halfLife);
          points.push(`${10 + t},${70 - n}`);
       }
       return "M " + points.join(" L ");
    },
    currentX() { return 10 + Math.min(120, this.time); },
    currentY() { return 70 - 60 * Math.pow(0.5, this.time / this.halfLife); }
  },
  methods: {
    initAtoms() {
      this.atoms = Array.from({ length: this.totalAtoms }, (_, i) => ({
        id: i,
        x: 30 + Math.random() * 340,
        y: 30 + Math.random() * 140,
        decayed: false,
        justDecayed: false,
        // Using inverse transform sampling: t = -ln(U) / lambda
        decayTime: -Math.log(Math.random()) * (this.halfLife / 0.693)
      }));
      this.activeCount = this.totalAtoms;
    },
    reset() {
      this.time = 0;
      this.initAtoms();
    },
    animate() {
      if (!this.running) return;
      this.time += 0.2;
      
      let count = 0;
      this.atoms.forEach(a => {
        if (!a.decayed && this.time >= a.decayTime) {
          a.decayed = true;
          a.justDecayed = true;
          setTimeout(() => { a.justDecayed = false; }, 400);
        }
        if (!a.decayed) count++;
      });
      this.activeCount = count;
      this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.initAtoms();
    this.animate();
  },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.animFrame);
  }
}
</script>

<style scoped lang="scss">
.radioactive-decay-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #f43f5e; }
      &.orange { border-left: 3px solid #fbbf24; }
    }
  }
  .mini-chart {
    position: absolute; bottom: 12px; right: 12px; background: rgba(15, 23, 42, 0.9); border-radius: 8px; border: 1px solid #1e293b; padding: 6px;
    label { display: block; text-align: center; font-size: 9px; color: #64748b; margin-top: 2px; }
  }
}

.decay-stats { font-size: 11px; font-weight: bold; .hl { color: #f43f5e; } }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .flex-1 { flex: 1; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; width: 40px; text-align: right; }

.discovery-card-compact {
  background: #fef2f2; padding: 10px 14px; border-radius: 10px; border: 1px solid #fee2e2; display: flex; align-items: center; gap: 10px;
  i { color: #dc2626; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #b91c1c; line-height: 1.5; strong { color: #991b1b; } }
}
</style>
